import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  reauthenticateWithPopup,
  linkWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInAnonymously,
  signOut as fbSignOut,
  onAuthStateChanged,
  connectAuthEmulator,
} from 'firebase/auth';

// Detect Tauri (desktop). On desktop we route Google OAuth through a native
// loopback-IP flow (browser → 127.0.0.1:<port>) instead of WebView2 popups
// or redirects — WebView2's Tracking Prevention blocks both.
export function isTauri() {
  return typeof window !== 'undefined' &&
    (window.__TAURI_INTERNALS__ || window.__TAURI__ || window.__TAURI_METADATA__);
}

export function appOrigin() {
  return isTauri() ? 'https://prezync.com' : window.location.origin;
}

const DESKTOP_CLIENT_ID = import.meta.env.VITE_GOOGLE_DESKTOP_CLIENT_ID;
const DESKTOP_CLIENT_SECRET = import.meta.env.VITE_GOOGLE_DESKTOP_CLIENT_SECRET;

// Runs the loopback OAuth flow in Tauri: opens the system browser to Google's
// consent screen, waits for the redirect to come back to 127.0.0.1, exchanges
// the code at Google's token endpoint (PKCE — no client secret needed).
// Returns { idToken, accessToken } or throws.
async function runTauriOAuth(scopes) {
  if (!DESKTOP_CLIENT_ID) {
    throw new Error('Desktop sign-in not configured — VITE_GOOGLE_DESKTOP_CLIENT_ID is missing.');
  }
  const { invoke } = await import('@tauri-apps/api/core');
  const { code, code_verifier, redirect_uri } = await invoke('do_google_oauth', {
    clientId: DESKTOP_CLIENT_ID,
    scopes,
  });

  const body = new URLSearchParams({
    client_id: DESKTOP_CLIENT_ID,
    client_secret: DESKTOP_CLIENT_SECRET,
    code,
    code_verifier,
    redirect_uri,
    grant_type: 'authorization_code',
  });
  const resp = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  const tokens = await resp.json();
  if (!resp.ok) {
    throw new Error(tokens.error_description || tokens.error || 'Token exchange failed.');
  }
  return { idToken: tokens.id_token, accessToken: tokens.access_token };
}

// Drive scope: drive.file lets us only see/edit files Prezync creates or the
// user opens via Drive picker. Less invasive than full drive scope.
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
let cachedDriveAccessToken = null;
let cachedDriveTokenExpiry = 0;
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from 'firebase/firestore';

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || undefined,
};

export const firebaseConfigured = Boolean(config.apiKey && config.projectId);
// Laser/zoom use Realtime Database when it's configured (see lib/live-pointer.js).
export const realtimePointerEnabled = firebaseConfigured && Boolean(config.databaseURL);

let app, auth, db;

if (firebaseConfigured) {
  app = initializeApp(config);
  auth = getAuth(app);
  // initializeFirestore (vs getFirestore) lets us turn on persistent IndexedDB
  // cache: read-and-write offline, queue mutations, replay on reconnect.
  // multi-tab manager so two open editor tabs don't fight over the cache lock.
  // Falls back to memory cache if IndexedDB isn't available (private mode).
  try {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch (err) {
    console.warn('[firestore] persistent cache failed, falling back to memory:', err);
    db = initializeFirestore(app, {});
  }
  // Local emulators for e2e tests (see tests/e2e) — never set in production.
  if (import.meta.env.VITE_USE_EMULATORS === 'true') {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
  }
}

export { app, auth, db };

export async function signInWithGoogle() {
  if (!firebaseConfigured) throw new Error('Firebase not configured');
  if (isTauri()) {
    const { idToken, accessToken } = await runTauriOAuth(['openid', 'email', 'profile']);
    const cred = GoogleAuthProvider.credential(idToken, accessToken);
    const result = await signInWithCredential(auth, cred);
    return result.user;
  }
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

export async function resetPassword(email) {
  if (!firebaseConfigured) throw new Error('Firebase not configured');
  await sendPasswordResetEmail(auth, email);
}

export async function signInWithEmail(email, password) {
  if (!firebaseConfigured) throw new Error('Firebase not configured');
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function signUpWithEmail(email, password) {
  if (!firebaseConfigured) throw new Error('Firebase not configured');
  const result = await createUserWithEmailAndPassword(auth, email, password);
  return result.user;
}

export async function signOut() {
  if (!firebaseConfigured) return;
  await fbSignOut(auth);
}

// Audience join: ensure there is *some* user (anonymous if unsigned) so we can
// write responses to Firestore under their UID.
export async function ensureAnonymousAuth() {
  if (!firebaseConfigured) throw new Error('Firebase not configured');
  if (auth.currentUser) return auth.currentUser;
  // Wait for the first auth state event in case there's a cached session
  await new Promise((resolve) => {
    const off = onAuthStateChanged(auth, () => { off(); resolve(); });
  });
  if (auth.currentUser) return auth.currentUser;
  const cred = await signInAnonymously(auth);
  return cred.user;
}

// Request (or refresh) a Google OAuth access token with the Drive scope.
// Pops the Google consent screen — instant if user has an active Google session
// and has previously granted the scope; otherwise shows the consent prompt.
// Returns the access token (string).
export async function requestDriveAccess() {
  if (!firebaseConfigured) throw new Error('Firebase not configured');
  if (isTauri()) {
    const { accessToken } = await runTauriOAuth([DRIVE_SCOPE]);
    if (!accessToken) throw new Error('No access token returned by Google.');
    cachedDriveAccessToken = accessToken;
    cachedDriveTokenExpiry = Date.now() + 55 * 60 * 1000;
    return cachedDriveAccessToken;
  }
  // Never signInWithPopup here: it would REPLACE the signed-in Prezync user with
  // whichever Google account is picked (an email/password user's decks vanish).
  // Google users re-authenticate with the Drive scope added; email/password
  // users link a Google account to the account they already have.
  const user = auth.currentUser;
  if (!user || user.isAnonymous) throw new Error('Sign in to upload files.');
  const provider = new GoogleAuthProvider();
  provider.addScope(DRIVE_SCOPE);
  const hasGoogle = user.providerData.some((p) => p.providerId === 'google.com');
  let result;
  try {
    result = hasGoogle ? await reauthenticateWithPopup(user, provider) : await linkWithPopup(user, provider);
  } catch (err) {
    throw driveAccessError(err, user);
  }
  const cred = GoogleAuthProvider.credentialFromResult(result);
  if (!cred?.accessToken) throw new Error('No access token returned by Google.');
  cachedDriveAccessToken = cred.accessToken;
  // Google access tokens last 1 hour; cache slightly less to be safe.
  cachedDriveTokenExpiry = Date.now() + 55 * 60 * 1000;
  return cachedDriveAccessToken;
}

function driveAccessError(err, user) {
  const code = err?.code || '';
  const messages = {
    'auth/user-mismatch': `Choose the Google account you signed in to Prezync with${user.email ? ` (${user.email})` : ''}.`,
    'auth/credential-already-in-use': 'That Google account already has its own Prezync account. Choose a different Google account to store your files in.',
    'auth/email-already-in-use': 'That Google account already has its own Prezync account. Choose a different Google account to store your files in.',
  };
  if (!messages[code]) return err;
  const friendly = new Error(messages[code]);
  friendly.code = code;
  return friendly;
}

// Returns a cached access token if still valid; otherwise pops the Google
// consent flow to get a fresh one.
export async function getDriveAccessToken({ forceRefresh = false } = {}) {
  if (!forceRefresh && cachedDriveAccessToken && Date.now() < cachedDriveTokenExpiry) {
    return cachedDriveAccessToken;
  }
  return requestDriveAccess();
}

// True when Drive can be used without showing Google's consent popup.
export function hasDriveAccessToken() {
  return !!cachedDriveAccessToken && Date.now() < cachedDriveTokenExpiry;
}

export function clearDriveAccessToken() {
  cachedDriveAccessToken = null;
  cachedDriveTokenExpiry = 0;
}

export function watchAuth(callback) {
  if (!firebaseConfigured) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
}
